import { loadConfig } from "@hobbyshopify/vite-cfg";
import { initializeApp } from "firebase/app";
import {
  type Auth,
  GoogleAuthProvider,
  getAuth,
  onAuthStateChanged,
  signInWithPopup,
  signOut,
  type User,
} from "firebase/auth";

/**
 * Firebase web config is public: Vite env (VITE_FIREBASE_*) on the host,
 * config.js written at container start in Docker (see @hobbyshopify/vite-cfg).
 */
export function createFirebaseAuth(): Auth {
  const app = initializeApp({
    apiKey: loadConfig("VITE_FIREBASE_API_KEY"),
    authDomain: loadConfig("VITE_FIREBASE_AUTH_DOMAIN"),
    projectId: loadConfig("VITE_FIREBASE_PROJECT_ID"),
    appId: loadConfig("VITE_FIREBASE_APP_ID"),
  });
  return getAuth(app);
}

export type AuthStage = "restoring" | "signedOut" | "signedIn";

export interface Session {
  watch(listener: (stage: AuthStage, user: User | null) => void): () => void;
  signInWithGoogle(): Promise<void>;
  signOut(): Promise<void>;
  /** The current ID token; Firebase refreshes it when it is about to lapse. */
  token(): Promise<string | undefined>;
}

export function firebaseSession(auth: Auth): Session {
  const google = new GoogleAuthProvider();
  return {
    watch: (listener) => onAuthStateChanged(auth, (user) => listener(user ? "signedIn" : "signedOut", user)),
    signInWithGoogle: async () => {
      await signInWithPopup(auth, google);
    },
    signOut: () => signOut(auth),
    token: () => auth.currentUser?.getIdToken() ?? Promise.resolve(undefined),
  };
}
